import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from "react-redux";
import AxiosServices from "../Services/axiosServices";
import MatchComponent, {LeaderboardRow, MatchBetRow, MatchPointsRow} from "./MatchComponent";
import GroupStandingsPair from "./GroupStandingsPair";
import BetsSidebarStats from "./BetsSidebarStats";
import ParieursProgressChart, {ProgressSeries} from "./ParieursProgressChart";
import {HasMatchStarted, IMatch, IsMatchInProgress} from "../Interfaces/IMatch";
import {useGlobal} from "../config/GlobalContext";
import {useAuth} from "../config/AuthContext";
import {RootState} from "../store";
import {useMatchEvents} from "../hooks/useMatchEvents";
import {matchAsUtcDate} from "../Services/datetime";

// Per-side label derived from a matchatt slot, used as a fallback
// when the match has no concrete matchteam yet:
//   G slot → "1er de A" / "2e de B" / …
//   M slot → "Winner 95"
//   3 slot → "3e FIFA"
// If a side carries multiple slots ("OR" predicate in the bracket),
// joins them with " ou ".
type MatchAttSlotRaw = {
    matchatt_idt: number;
    match_idt: number;
    slot_idt: number | null;
    dom_ext: 'D' | 'E' | null;
    slot_type: 'G' | 'M' | '3' | null;
    valeur: string | null;
    numero: number | null;
};
const slotShortLabel = (s: {slot_type: 'G' | 'M' | '3'; valeur: string; numero: number}): string => {
    if (s.slot_type === 'G') {
        const suffix = s.numero === 1 ? 'er' : 'e';
        return `${s.numero}${suffix} de ${s.valeur}`;
    }
    if (s.slot_type === 'M') {
        const head = (s.valeur ?? 'W').toUpperCase() === 'L' ? 'Perdant' : 'Vainqueur';
        return `${head} du match n°${s.numero}`;
    }
    return '3e FIFA';
};

const Matchs: FC = () => {

    const [matches, setMatches] = useState<IMatch[]>([]);
    const [matchAttRows, setMatchAttRows] = useState<MatchAttSlotRaw[]>([]);
    const [pointsRows, setPointsRows] = useState<MatchPointsRow[]>([]);
    const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
    const [betRows, setBetRows] = useState<MatchBetRow[]>([]);
    const [liveNonce, setLiveNonce] = useState(0);
    const {filteredMatches, currentCmpEdt, groups, hasGroups, selectedGroupIds, showOld} = useGlobal();
    const {authUser} = useAuth();
    const userBets = useSelector((state: RootState) => state.bets.userBets);

    const refetchMatches = useCallback(() => {
        if (!currentCmpEdt) return;
        AxiosServices.getMatches(setMatches, currentCmpEdt)
            .then()
            .catch(err => console.error(err));
        // Knockout matches whose teams aren't pinned to matchteam yet
        // need their matchatt slots to be rendered with placeholder
        // labels ("Winner 95", "1er de A", …) so they don't drop out
        // of the public list.
        AxiosServices.matchAttListByCmpEdt(currentCmpEdt)
            .then(rows => setMatchAttRows(rows))
            .catch(err => {
                console.error('matchAttListByCmpEdt failed', err);
                setMatchAttRows([]);
            });
    }, [currentCmpEdt]);

    const refetchPoints = useCallback(() => {
        if (!currentCmpEdt) {
            setPointsRows([]);
            return;
        }
        AxiosServices.getPointsByMatch(currentCmpEdt)
            .then(rows => setPointsRows(rows.map(r => ({...r, total: Number(r.total)}))))
            .catch(err => {
                console.error('getPointsByMatch failed', err);
                setPointsRows([]);
            });
    }, [currentCmpEdt]);

    const refetchLeaderboard = useCallback(() => {
        if (!currentCmpEdt) {
            setLeaderboard([]);
            return;
        }
        AxiosServices.getLeaderboard(currentCmpEdt)
            .then(rows => setLeaderboard(rows.map(r => ({...r, total: Number(r.total)}))))
            .catch(err => {
                console.error('getLeaderboard failed', err);
                setLeaderboard([]);
            });
    }, [currentCmpEdt]);

    const refetchBets = useCallback(() => {
        if (!currentCmpEdt) {
            setBetRows([]);
            return;
        }
        AxiosServices.getBetsByMatch(currentCmpEdt)
            .then(rows => setBetRows(rows))
            .catch(err => {
                console.error('getBetsByMatch failed', err);
                setBetRows([]);
            });
    }, [currentCmpEdt]);

    useEffect(() => {
        refetchMatches();
    }, [refetchMatches]);

    useEffect(() => {
        refetchPoints();
        refetchLeaderboard();
        refetchBets();
    }, [refetchPoints, refetchLeaderboard, refetchBets, userBets]);

    useMatchEvents(currentCmpEdt, {
        debounceMs: 400,
        onScoreChanged: () => {
            refetchMatches();
            refetchPoints();
            refetchLeaderboard();
            refetchBets();
            setLiveNonce(n => n + 1);
        },
    });

    // Watch for matches that kick off while the tab is open: every 30 s we
    // check whether any match's kickoff fell into the last interval, and if
    // so refresh everything so the predictions become visible without a
    // page reload.
    const lastKickoffTickRef = useRef<number>(Date.now());
    useEffect(() => {
        const id = setInterval(() => {
            const now = Date.now();
            const since = lastKickoffTickRef.current;
            lastKickoffTickRef.current = now;
            const justKickedOff = matches.some(m => {
                const t = matchAsUtcDate(m.Dte, m.Hre)?.getTime();
                return typeof t === 'number' && t > since && t <= now;
            });
            if (justKickedOff) {
                refetchMatches();
                refetchPoints();
                refetchLeaderboard();
                refetchBets();
            }
        }, 30000);
        return () => clearInterval(id);
    }, [matches, refetchMatches, refetchPoints, refetchLeaderboard, refetchBets]);

    const pointsByMatchNum = useMemo(() => {
        const m = new Map<number, MatchPointsRow[]>();
        for (const r of pointsRows) {
            const key = Number(r.match_num);
            if (!m.has(key)) m.set(key, []);
            m.get(key)!.push(r);
        }
        m.forEach(arr => {
            arr.sort((a, b) => Number(b.total) - Number(a.total) || a.parieur.localeCompare(b.parieur));
        });
        return m;
    }, [pointsRows]);

    // For each match num, keyed by usr_idt → the parieur's bet object so
    // MatchComponent can read it in O(1) when rendering each row.
    const betsByMatchNum = useMemo(() => {
        const m = new Map<number, Map<number, MatchBetRow>>();
        for (const r of betRows) {
            const key = Number(r.match_num);
            if (!m.has(key)) m.set(key, new Map());
            m.get(key)!.set(Number(r.usr_idt), r);
        }
        return m;
    }, [betRows]);

    // Idt of the first upcoming match (kickoff strictly in the future).
    // The points card is shown on that one too so the leaderboard is
    // already visible before kickoff.
    const nextUpcomingIdt = useMemo(() => {
        const now = Date.now();
        const upcoming = matches
            .map(m => ({m, t: matchAsUtcDate(m.Dte, m.Hre)?.getTime() ?? NaN}))
            .filter(x => Number.isFinite(x.t) && x.t > now && !HasMatchStarted(x.m))
            .sort((a, b) => a.t - b.t);
        return upcoming[0]?.m.Idt;
    }, [matches]);

    // Idt of the "focus" match — currently in progress wins; otherwise
    // falls back on the next upcoming. Drives the auto-scroll when the
    // user toggles showOld so they don't land on the very first match
    // played weeks ago.
    const focusMatchIdt = useMemo(() => {
        const inProgress = matches.find(m => IsMatchInProgress(m));
        return inProgress?.Idt ?? nextUpcomingIdt;
    }, [matches, nextUpcomingIdt]);

    // Progression chart data: for each played journée (= journée whose
    // earliest kickoff is in the past), compute every leaderboard user's
    // cumulative points up to and including that journée. Journées are
    // ordered by their earliest kickoff time.
    const progression = useMemo<{journeeLabels: string[]; series: ProgressSeries[]} | null>(() => {
        if (leaderboard.length === 0) return null;

        const journeeFirstStart = new Map<string, number>();
        for (const m of matches) {
            const lib = m.Journee_Lib;
            if (!lib) continue;
            const t = matchAsUtcDate(m.Dte, m.Hre)?.getTime();
            if (typeof t !== 'number' || !Number.isFinite(t)) continue;
            const prev = journeeFirstStart.get(lib);
            if (prev == null || t < prev) journeeFirstStart.set(lib, t);
        }

        const now = Date.now();
        const journeeLabels = Array.from(journeeFirstStart.entries())
            .filter(([_, t]) => t <= now)
            .sort((a, b) => a[1] - b[1])
            .map(([lib]) => lib);
        if (journeeLabels.length === 0) return null;

        const matchesByJournee = new Map<string, IMatch[]>();
        for (const m of matches) {
            if (!journeeLabels.includes(m.Journee_Lib)) continue;
            const arr = matchesByJournee.get(m.Journee_Lib) ?? [];
            arr.push(m);
            matchesByJournee.set(m.Journee_Lib, arr);
        }

        const pointsByNumByUsr = new Map<number, Map<number, number>>();
        for (const r of pointsRows) {
            const num = Number(r.match_num);
            if (!pointsByNumByUsr.has(num)) pointsByNumByUsr.set(num, new Map());
            pointsByNumByUsr.get(num)!.set(Number(r.usr_idt), Number(r.total));
        }

        const cum = new Map<number, number>();
        const me = authUser?.idt;
        const series: ProgressSeries[] = leaderboard.map(r => ({
            usr_idt: Number(r.usr_idt),
            parieur: r.parieur,
            points: [],
            isMe: !!me && Number(r.usr_idt) === Number(me),
        }));

        for (const lib of journeeLabels) {
            const ms = matchesByJournee.get(lib) ?? [];
            for (const m of ms) {
                if (!HasMatchStarted(m)) continue;
                const map = pointsByNumByUsr.get(Number(m.Num));
                if (!map) continue;
                map.forEach((v, k) => cum.set(k, (cum.get(k) ?? 0) + v));
            }
            for (const s of series) s.points.push(cum.get(s.usr_idt) ?? 0);
        }
        return {journeeLabels, series};
    }, [matches, pointsRows, leaderboard, authUser?.idt]);

    // Per-match snapshot of the running leaderboard. We walk matches in
    // chronological order and accumulate per-match points; the snapshot
    // for match X reflects "standings right after X if X has been played,
    // else right before X". That way an old match's ranking never folds
    // in points earned on later matches.
    //
    // When several matches share the same kickoff timestamp (e.g. two
    // group-stage games at 21:00), they're treated as one round: all of
    // them must contribute to the snapshot together, so the rank shown
    // on every card in that batch already includes the points earned on
    // its siblings. Without this, the first card in numerical order
    // would lag the others.
    const cumulativeByMatchIdt = useMemo(() => {
        const result = new Map<number, Map<number, number>>();
        const sorted = matches
            .map(m => {
                const t = matchAsUtcDate(m.Dte, m.Hre)?.getTime();
                return {m, t: typeof t === 'number' && Number.isFinite(t) ? t : Number.POSITIVE_INFINITY};
            })
            .sort((a, b) => a.t - b.t || a.m.Num - b.m.Num);

        const rowsByNum = new Map<number, MatchPointsRow[]>();
        for (const r of pointsRows) {
            const key = Number(r.match_num);
            if (!rowsByNum.has(key)) rowsByNum.set(key, []);
            rowsByNum.get(key)!.push(r);
        }

        const cum = new Map<number, number>();
        let i = 0;
        while (i < sorted.length) {
            const batchTime = sorted[i].t;
            const batch: typeof sorted = [];
            while (i < sorted.length && sorted[i].t === batchTime) {
                batch.push(sorted[i]);
                i++;
            }
            for (const {m} of batch) {
                if (!HasMatchStarted(m)) continue;
                const rows = rowsByNum.get(Number(m.Num)) ?? [];
                for (const r of rows) {
                    const key = Number(r.usr_idt);
                    cum.set(key, (cum.get(key) ?? 0) + Number(r.total));
                }
            }
            const snapshot = new Map(cum);
            for (const {m} of batch) result.set(m.Idt, snapshot);
        }
        return result;
    }, [matches, pointsRows]);

    const selectedGroupCodes = groups
        .filter(g => selectedGroupIds.includes(g.Idt))
        .map(g => g.Grp_Cod)
        .filter(Boolean) as string[];

    const visibleMatches = matches
        .filter(match => !filteredMatches || match.Journee_Lib === filteredMatches)
        .filter(match =>
            !hasGroups ||
            !match.Grp_Cod ||
            selectedGroupCodes.length === 0 ||
            selectedGroupCodes.includes(match.Grp_Cod)
        );

    // For each match × side, the placeholder label derived from any
    // configured matchatt slots. Empty when no slot is configured.
    const slotLabelByMatchIdt = useMemo(() => {
        const map = new Map<number, {D: string; E: string}>();
        const bySide = new Map<number, {D: string[]; E: string[]}>();
        for (const r of matchAttRows) {
            if (r.slot_idt == null || !r.dom_ext || !r.slot_type || r.valeur == null || r.numero == null) continue;
            const key = Number(r.match_idt);
            if (!bySide.has(key)) bySide.set(key, {D: [], E: []});
            bySide.get(key)![r.dom_ext].push(slotShortLabel({
                slot_type: r.slot_type,
                valeur: r.valeur,
                numero: Number(r.numero),
            }));
        }
        bySide.forEach((sides, key) => {
            map.set(key, {
                D: sides.D.join(' ou '),
                E: sides.E.join(' ou '),
            });
        });
        return map;
    }, [matchAttRows]);

    // Replace missing team names with the slot-derived label so the
    // public list can still render the round of 16 (and later phases)
    // before the admin pins concrete matchteam rows.
    const enrichedVisibleMatches = useMemo(() => {
        return visibleMatches.map(m => {
            if (m.Nom_Dom && m.Nom_Ext) return m;
            const slot = slotLabelByMatchIdt.get(m.Idt);
            if (!slot) return m;
            return {
                ...m,
                Nom_Dom: m.Nom_Dom || slot.D || m.Nom_Dom,
                Nom_Ext: m.Nom_Ext || slot.E || m.Nom_Ext,
            };
        });
    }, [visibleMatches, slotLabelByMatchIdt]);

    const completeMatches = enrichedVisibleMatches
        .filter(match => match.Nom_Dom && match.Nom_Ext)
        // Display in kickoff order rather than match number — for a
        // tournament whose numbering doesn't follow the calendar
        // (group stage interleaved with knock-out, replays, etc.) this
        // tracks what's actually upcoming or just played.
        .sort((a, b) => {
            const ta = matchAsUtcDate(a.Dte, a.Hre)?.getTime() ?? Number.POSITIVE_INFINITY;
            const tb = matchAsUtcDate(b.Dte, b.Hre)?.getTime() ?? Number.POSITIVE_INFINITY;
            return ta - tb || (a.Num ?? 0) - (b.Num ?? 0);
        });

    // Scroll the matches list to the in-progress / next-upcoming card
    // when the user toggles "Montrer les anciens matchs": the freshly
    // un-hidden past rows would otherwise push the focus all the way to
    // match #1. Triggered on the rising edge of showOld and whenever
    // the focus target changes while old matches are visible.
    const matchesScrollRef = useRef<HTMLDivElement>(null);
    const matchRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const prevShowOldRef = useRef<boolean>(showOld);
    useEffect(() => {
        const rose = !prevShowOldRef.current && showOld;
        prevShowOldRef.current = showOld;
        if (!showOld || focusMatchIdt == null) return;
        if (!rose && !completeMatches.length) return;
        const node = matchRefs.current.get(focusMatchIdt);
        const container = matchesScrollRef.current;
        if (!node || !container) return;
        // Wait one frame so the new rows have been laid out before we
        // measure positions.
        requestAnimationFrame(() => {
            const top = node.offsetTop - container.offsetTop;
            container.scrollTo({top, behavior: 'smooth'});
        });
    }, [showOld, focusMatchIdt, completeMatches.length]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                height: '100%',
                width: '100%',
                gap: 8,
            }}
        >
            <aside
                style={{
                    width: '28%',
                    minWidth: 260,
                    maxWidth: 560,
                    overflowY: 'auto',
                    background: 'rgba(255,255,255,0.03)',
                    borderRight: '1px solid rgba(255,255,255,0.08)',
                }}
            >
                <div
                    style={{
                        padding: '8px 12px 4px',
                        fontSize: 11,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.6)',
                    }}
                >
                    Classements — officiel vs mes paris
                </div>
                <GroupStandingsPair refreshKey={liveNonce}/>
            </aside>

            <main
                style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                <div
                    style={{
                        flexShrink: 0,
                        background: '#203923',
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                    }}
                >
                    <BetsSidebarStats matches={matches} userBets={userBets}/>
                </div>
                <div ref={matchesScrollRef} className="overview" style={{flex: 1, minHeight: 0, overflowY: 'auto'}}>
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                        {completeMatches.map((match: IMatch) => (
                            <div
                                key={match.Idt}
                                ref={(el) => {
                                    if (el) matchRefs.current.set(match.Idt, el);
                                    else matchRefs.current.delete(match.Idt);
                                }}
                                style={{display: 'grid', gridTemplateColumns: '390px 1fr', columnGap: 24, alignItems: 'stretch'}}
                            >
                                <MatchComponent
                                    match={match}
                                    pointsRows={pointsByMatchNum.get(match.Num) ?? []}
                                    betsByUser={betsByMatchNum.get(match.Num)}
                                    leaderboard={leaderboard}
                                    cumulativeByUser={cumulativeByMatchIdt.get(match.Idt)}
                                    isNextUpcoming={match.Idt === nextUpcomingIdt}
                                />
                            </div>
                        ))}
                        {progression && (
                            <div style={{display: 'grid', gridTemplateColumns: '390px 1fr', columnGap: 24}}>
                                <div style={{padding: 8}}>
                                    <ParieursProgressChart
                                        journeeLabels={progression.journeeLabels}
                                        series={progression.series}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

export default Matchs;
